/** The `feed` area: the Activity page and its feed, and an item's activity.
 *  Owned by the stage 2 slot components-b (docs/I18N.md §11). */
export const feed = {
  // --- The level filter and the signal words ----------------------------------
  'feed.filter.all': 'All',
  'feed.filter.flagged': 'Flagged',
  'feed.filter.critical': 'Critical',
  'feed.filter.byLevel': 'Filter by level',
  'feed.filter.activityByLevel': 'Filter activity by level',
  /** The badge a notable entry wears (warning and critical only). */
  'feed.signal.flagged': 'flagged',
  'feed.signal.critical': 'critical',
  /** An event's level on its detail page, as the log stores it. */
  'feed.level.info': 'info',
  'feed.level.notice': 'notice',
  'feed.level.warning': 'warning',
  'feed.level.critical': 'critical',

  // --- An item's activity (activity-feed.tsx) ---------------------------------
  'feed.trail.heading': 'Activity',
  'feed.trail.empty': 'No activity yet. Every move on this item lands here.',
  /** The History's scope switch: the item's own acts, or with its sub-items'. */
  'feed.trail.scope.label': 'Show activity of',
  'feed.trail.scope.own': 'This item',
  'feed.trail.scope.subItems': 'With sub-items',
  'feed.trail.emptyAtLevel': 'Nothing at this level. Lower the filter to see routine activity.',

  // --- The Activity page and its feed (feed-view.tsx) -------------------------
  'feed.page.title': 'Activity',
  'feed.page.placeholder': 'Open an entry to read its detail here.',
  'feed.empty': 'Nothing here yet.',
  'feed.emptyAtLevel': 'Nothing at this level, and no open asks.',
  'feed.hideMuted': 'Hide muted',
  'feed.showAll': 'Show all ({count})',
  'feed.unread': 'Unread',
  'feed.markAllRead': 'Mark all read',
  'feed.undoMarkAllRead': 'Undo: mark all read',
  'feed.undo': 'Undo',
  'feed.markReadFailed': 'Could not mark read.',
  'feed.undoFailed': 'Could not undo.',
  /** The chip on a standing ask: a question addressed to you. */
  'feed.askChip': 'ask',
  'feed.row.aPlan': 'a plan',
  'feed.row.open': 'Open {title} details',
  'feed.row.openUntitled': 'Open entry details',
  'feed.row.openBurst': {
    one: 'Open {count} change on {title}',
    other: 'Open {count} changes on {title}',
  },
  'feed.row.openBurstUntitled': {
    one: 'Open {count} change on this item',
    other: 'Open {count} changes on this item',
  },
  'feed.row.acknowledge': 'Acknowledge',
  'feed.row.acknowledgeItem': 'Acknowledge {title}',
  'feed.row.acknowledgeUntitled': 'Acknowledge this item',
  'feed.row.notifications': 'Notifications for {title}',
  'feed.row.notificationsUntitled': 'Notifications for this item',
  'feed.notify.heading': 'Notify me about this item',
  'feed.notify.subscribe': 'Subscribe',
  'feed.notify.default': 'Default',
  'feed.notify.hide': 'Hide',

  // --- The clipped border (HS-K5): a dependency the viewer cannot see -----------
  /** It was closed with the work marked done. */
  'feed.clipped.done': "A dependency you can't see was closed",
  /** It was closed without the work marked done. */
  'feed.clipped.cancelled': "A dependency you can't see was cancelled",
  /** It was closed and is open again. */
  'feed.clipped.reopened': "A dependency you can't see was reopened",
  /** Its end moved past this item's end; neither date is shown. */
  'feed.clipped.endsAfter': "A dependency you can't see now ends after this item's end",

  // --- The asks addressed to you ----------------------------------------------
  'feed.ask.contactRequest': 'Contact request',
  /** `actor`: the requester's name, and their handle or address after it. */
  'feed.ask.wantsContact': '{actor} wants to be your contact',
  'feed.ask.addedYou': '{adder} added you to {group}',
  'feed.ask.youWereAdded': 'You were added to {group}',
  'feed.ask.accept': 'Accept',
  'feed.ask.decline': 'Decline',
  /** Accepting a seat offer (its sentence says "assigned you"). */
  'feed.ask.takeUp': 'Accept',
  'feed.ask.options': 'Options for {name}',
  'feed.ask.block': 'Block',
  'feed.ask.blockRequest': 'Declines this request. No request passes either way until you unblock.',
  'feed.ask.blockInvitations':
    'Hides their invitations. No request passes either way until you unblock.',
  /** `detail`: the raised change, or the act's own sentence. */
  'feed.decision.asks': '{actor} asks {detail}',
  'feed.decision.proposes': '{actor} proposes {detail}',
  /** A seat offered to the viewer, or to a group of theirs (`group`), read as
   *  an assignment: who offers, the item, then the seat as a chip ({role},
   *  `feed.offer.role.*`). One sentence on every surface, the item's own page
   *  included (src/components/seat-offer.tsx: the feed row, the item's
   *  attention band, the board card; Leon, 2026-10-15). */
  'feed.offer.you': '{actor} assigned you {item} {role}',
  /** The viewer named in an item's prose (docs/PROSE.md §5.4): who named them
   *  and the item, pinned until they open it. */
  'feed.event.mentioned': '{actor} named you in {item}',
  'feed.offer.group': '{actor} assigned {group} {item} {role}',
  /** The seat's chip in an offer, and its plain label in an offer's push. */
  'feed.offer.role.requester': 'Requested by',
  'feed.offer.role.contributor': 'Contributor',

  // --- A folded burst ---------------------------------------------------------
  /** The burst row's subject: "Ari", "Ari and Blake", "Ari, Blake and 2 others". */
  'feed.names.someone': 'Someone',
  'feed.names.two': '{first} and {second}',
  'feed.names.more': {
    one: '{first}, {second} and {count} other',
    other: '{first}, {second} and {count} others',
  },
  /** `summary`: the burst's distinct action labels, after a colon. */
  'feed.burst.made': {
    one: '{names} made {count} change{summary}',
    other: '{names} made {count} changes{summary}',
  },
  'feed.burst.changes': { one: '{count} change', other: '{count} changes' },

  // --- An entry's detail page -------------------------------------------------
  'feed.detail.proposedChange': 'Proposed change',
  'feed.detail.changes': 'Changes',
  'feed.detail.event': 'Event',
  'feed.detail.on': 'On',
  'feed.detail.proposedBy': 'Proposed by',
  'feed.detail.via': 'via {proposal}',
  'feed.detail.change': 'Change',
  'feed.detail.status': 'Status',
  'feed.detail.who': 'Who',
  'feed.detail.when': 'When',
  'feed.detail.level': 'Level',
  'feed.detail.openChange': 'Open this change',
  'feed.detail.ownerOnly': 'Only the item’s requester can resolve this.',
  'feed.detail.accept': 'Accept',
  'feed.detail.decline': 'Decline',
  'feed.changeStatus.pending': 'awaiting a decision',
  'feed.changeStatus.applied': 'applied',
  'feed.changeStatus.declined': 'declined',
  'feed.changeStatus.superseded': 'superseded',
  'feed.diff.aligned': 'the live value already matches',
  'feed.diff.adapted': 'the live value has moved since this was proposed',
  'feed.diff.unmet': 'not yet met',

  // --- Where an entry lives (where.tsx) ---------------------------------------
  'feed.where.removed': '(removed item)',

  // --- An entry's parts (src/domain/entries.ts) --------------------------------
  /** The retired platform phase words, kept so old `phase_changed` rows read. */
  'feed.change.phase.open': 'To do',
  'feed.change.phase.active': 'In progress',
  'feed.change.phase.done': 'Done',
  'feed.change.phase.cancelled': 'Cancelled',
  /** A flow's machine actor, leading an entry: the flow's sign, these words. */
  'feed.entry.lead.flow': 'Flow {name}',
  'feed.entry.lead.anyFlow': 'A flow',
  /** Two parts of one entry side by side, in its accessible sentence. */
  'feed.entry.join': '{first} {second}',
  /** A field emptied: it follows the field's label. */
  'feed.entry.field.line': 'Line',
  'feed.entry.line.ticked': 'ticked',
  'feed.entry.line.unticked': 'unticked',
  'feed.entry.line.moved': 'moved',
  'feed.entry.line.promoted': 'Line made a sub-item',
  'feed.entry.line.demoted': 'Sub-item made a line',
  'feed.entry.cleared': 'cleared',
  /** The names an entry puts before a value. */
  'feed.entry.field.title': 'Title',
  'feed.entry.field.description': 'Description',
  'feed.entry.field.acceptanceCriteria': 'Acceptance criteria',
  'feed.entry.field.note': 'Note',
  'feed.entry.field.priority': 'Priority',
  'feed.entry.field.visibility': 'Visibility',
  'feed.entry.field.access': 'Access',
  'feed.entry.field.share': 'Share',
  'feed.entry.field.allocation': 'Allocation',
  'feed.entry.field.allocationUnit': 'Allocation unit',
  'feed.entry.field.allocationCadence': 'Allocation cadence',
  'feed.entry.field.estimate': 'Estimate',
  'feed.entry.field.estimateUnit': 'Estimate unit',
  'feed.entry.field.budget': 'Budget',
  'feed.entry.field.budgetCurrency': 'Currency',
  'feed.entry.field.budgetCadence': 'Budget cadence',
  'feed.entry.field.start': 'Start',
  'feed.entry.field.startAnchor': 'Start anchor',
  'feed.entry.field.startOffset': 'Start offset',
  'feed.entry.field.startOffsetUnit': 'Start offset unit',
  'feed.entry.field.end': 'End',
  'feed.entry.field.endAnchor': 'End anchor',
  'feed.entry.field.endOffset': 'End offset',
  'feed.entry.field.endOffsetUnit': 'End offset unit',
  'feed.entry.field.duration': 'Duration',
  'feed.entry.field.durationUnit': 'Duration unit',
  'feed.entry.field.cadence': 'Cadence',
  'feed.entry.field.cadenceUnit': 'Cadence unit',
  'feed.entry.seat.requester': 'Requester',
  'feed.entry.seat.contributor': 'Contributor',
  /** A relationship, as the entry's item has it. */
  'feed.entry.relation.dependsOn': 'Depends on',
  'feed.entry.relation.neededBy': 'Needed by',
  'feed.entry.relation.partOf': 'Part of',
  'feed.entry.relation.subItems': 'Sub-items',
  'feed.entry.relation.related': 'Related',
  /** The stored choices an entry shows, in the words their controls use. */
  'feed.entry.role.viewer': 'Viewer',
  'feed.entry.role.member': 'Member',
  'feed.entry.role.owner': 'Owner',
  'feed.entry.priority.must': 'must',
  'feed.entry.priority.should': 'should',
  'feed.entry.priority.could': 'could',
  'feed.entry.visibility.open': 'Open to everyone',
  'feed.entry.visibility.protected': 'Private',
  'feed.entry.access.inherits': 'Inherits from parents',
  'feed.entry.access.broken': 'Chain broken',
  'feed.entry.module.resources': 'Resources',
  'feed.entry.module.schedule': 'Schedule',
  'feed.entry.unit.fte': 'FTE',
  'feed.entry.unit.percent': '%',
  'feed.entry.unit.hours': 'hours',
  'feed.entry.unit.days': 'days',
  'feed.entry.unit.weeks': 'weeks',
  'feed.entry.unit.months': 'months',
  'feed.entry.cadence.oneTime': 'one-time',
  'feed.entry.cadence.perWeek': 'per week',
  'feed.entry.cadence.perMonth': 'per month',
  'feed.entry.cadence.perYear': 'per year',

  // --- Each event action: its label, and the sentence it makes ----------------
  // The label stands alone (a burst's summary lists them); the sentence names
  // the actor and the change the action made (`detail`, which may be empty).
  'feed.action.created': 'created',
  'feed.action.statusChanged': 'moved',
  'feed.action.committed': 'committed',
  'feed.action.phaseChanged': 'moved',
  'feed.action.tendered': 'handed in the work',
  'feed.action.tenderRetracted': 'took the work back',
  'feed.action.dissolved': 'cancelled',
  'feed.action.revived': 'reopened',
  'feed.action.activityDeclared': 'declared',
  'feed.action.concluded': 'finished',
  'feed.action.cancelled': 'cancelled',
  'feed.action.reopened': 'reopened',
  'feed.action.seatOffered': 'offered a seat',
  'feed.action.seatTaken': 'took up a seat',
  'feed.action.seatDeclined': 'declined a seat',
  'feed.action.seatWithdrawn': 'withdrew a seat offer',
  'feed.action.seatAccepted': 'accepted a seat offer',
  /** A seat event's word in a folded burst: the seat, then what happened to it
   *  (src/domain/entries.ts `SEAT_EVENT`). */
  'feed.action.seatProposedRequester': 'requester proposed',
  'feed.action.seatProposedContributor': 'contributor proposed',
  'feed.action.seatAssignedRequester': 'requester assigned',
  'feed.action.seatAssignedContributor': 'contributor assigned',
  'feed.action.seatAcceptedRequester': 'requester accepted',
  'feed.action.seatAcceptedContributor': 'contributor accepted',
  'feed.action.seatAcceptedForRequester': 'requester accepted for the group',
  'feed.action.seatAcceptedForContributor': 'contributor accepted for the group',
  'feed.action.seatTookUpRequester': 'requester taken up',
  'feed.action.seatTookUpContributor': 'contributor taken up',
  'feed.action.seatRefusedRequester': 'requester declined',
  'feed.action.seatRefusedContributor': 'contributor declined',
  'feed.action.pushRuleSet': 'set assignment rules',
  'feed.action.reassigned': 'reassigned',
  'feed.action.fieldChanged': 'edited',
  'feed.action.contentEdited': 'edited',
  'feed.action.commented': 'commented',
  'feed.action.acknowledged': 'acknowledged',
  'feed.action.resolved': 'cleared the verdict',
  'feed.action.accepted': 'accepted',
  'feed.action.disputed': 'denied',
  'feed.action.closed': 'closed',
  'feed.action.aborted': 'aborted',
  'feed.action.dependencyAdded': 'added dependency',
  'feed.action.dependencyRemoved': 'removed dependency',
  'feed.action.reparented': 'moved',
  'feed.action.relationshipAdded': 'linked',
  'feed.action.relationshipRemoved': 'unlinked',
  'feed.action.relationshipProposed': 'requested a link',
  'feed.action.relationshipAccepted': 'accepted a link',
  'feed.action.relationshipRejected': 'declined a link',
  'feed.action.shared': 'granted a role',
  'feed.action.unshared': 'revoked a role',
  'feed.action.accessRequested': 'requested access',
  'feed.action.stageAdvanced': 'advanced a stage',
  'feed.action.memberAdded': 'added a member',
  'feed.action.memberRemoved': 'removed a member',
  'feed.action.nestingProposed': 'proposed nesting',
  'feed.action.nestingAccepted': 'accepted nesting',
  'feed.action.nestingSevered': 'severed nesting',
  'feed.action.memberProposed': 'asked a person to join',
  'feed.action.memberAccepted': 'joined',
  'feed.action.memberDeclined': 'declined to join',
  'feed.action.grantIssued': 'granted access',
  'feed.action.grantRevoked': 'revoked access',
  'feed.action.moduleEnabled': 'enabled a module',
  'feed.action.moduleDisabled': 'disabled a module',
  'feed.action.moduleAdded': 'added a module',
  'feed.action.moduleRemoved': 'removed a module',
  'feed.action.tagDefined': 'defined a tag',
  'feed.action.tagApplied': 'applied a tag',
  'feed.action.tagCleared': 'cleared a tag',
  'feed.action.factQualified': 'qualified a fact',
  'feed.action.factAdopted': 'set',
  'feed.action.factContained': 'not affected by',
  /** The same act where no other item is cited (a finding): no "by". */
  'feed.action.factContainedAlone': 'not affected',
  'feed.action.flowFinding': 'flagged a rule',
  'feed.action.factCrossed': 'crossed a boundary',
  'feed.action.viewDefined': 'saved a board or a view',
  'feed.action.viewUpdated': 'updated a board or a view',
  'feed.action.viewDeleted': 'deleted a board',
  'feed.action.viewRetired': 'archived a board or a view',
  'feed.action.viewRestored': 'restored a board or a view',
  'feed.action.viewAccessGranted': 'shared a board or a view',
  'feed.action.viewAccessRevoked': 'unshared a board or a view',
  'feed.action.proposalCreated': 'drafted a proposal',
  'feed.action.proposalPublished': 'published a proposal',
  'feed.action.proposalWithdrawn': 'withdrew a proposal',
  'feed.action.changeProposed': 'proposed a change',
  'feed.action.changeApplied': 'applied a change',
  'feed.action.changeDeclined': 'declined a proposed change',
  'feed.action.changeSuperseded': 'superseded a proposed change',
  'feed.action.planCreated': 'created a plan',
  'feed.action.proposalAddedToPlan': 'added a proposal to a plan',
  'feed.action.planPublished': 'published a plan',
  'feed.action.definitionDefined': 'defined a flow',
  'feed.action.definitionChanged': 'changed a definition',
  'feed.action.definitionArchived': 'archived a definition',
  'feed.action.definitionUnarchived': 'un-archived a definition',
  'feed.action.definitionAccessGranted': 'granted access to a flow',
  'feed.action.definitionAccessRevoked': 'revoked access to a flow',
  'feed.action.definitionAdopted': 'adopted a definition',
  'feed.action.definitionForked': 'forked a definition',
  'feed.action.definitionLinked': 'declared an alignment',
  'feed.action.definitionUnlinked': 'removed an alignment',
  'feed.action.annotationChanged': 'noted',
  'feed.action.itemPinned': 'pinned to board',
  'feed.action.itemUnpinned': 'unpinned from board',
  'feed.action.flowJoined': 'joined a flow',
  'feed.action.flowLeft': 'left a flow',
  'feed.action.flowAdvanced': 'advanced in a flow',
  'feed.action.flowStageMoved': 'moved a stage',
  'feed.action.flowWaived': 'waived a condition',
  'feed.action.flowConditionAdded': 'added a condition',
  'feed.action.elevated': 'elevated (override mode)',
  'feed.action.deElevated': 'ended override mode',
  'feed.action.decisionRecorded': 'recorded a decision',
  'feed.action.ceremonyConvened': 'convened a ceremony',
  'feed.action.ceremonyItemAdded': 'added a decision to the agenda',
  'feed.action.ceremonyItemExecuted': 'took a decision',
  'feed.action.ceremonyItemDeclined': 'declined a decision',
  'feed.action.ceremonyClosed': 'closed a ceremony',
  'feed.action.ceremonyRosterChanged': 'changed a ceremony role',
  'feed.action.inviteCreated': 'invited someone by email',
  'feed.action.inviteRevoked': 'revoked an invite',
  'feed.action.inviteClaimed': 'joined by invite',
  'feed.action.inviteExpired': 'let an invite expire',
  'feed.action.machineKeyMinted': 'minted an agent key',
  'feed.action.machineKeyRevoked': 'revoked an agent key',
  'feed.action.childrenReordered': 'reordered children',
  'feed.action.closeDeferred': 'deferred the close',
  'feed.action.closeDeferralRetracted': 'took back the deferred close',
  'feed.action.itemTagged': 'tagged',
  'feed.action.itemUntagged': 'removed tag',
  'feed.action.attachmentAdded': 'added a file',
  'feed.action.attachmentRemoved': 'removed a file',
  'feed.action.attachmentCollected': "deleted a file's contents",

  'feed.event.created': '{actor} created {detail}',
  'feed.event.statusChanged': '{actor} moved {detail}',
  'feed.event.committed': '{actor} committed {detail}',
  'feed.event.phaseChanged': '{actor} moved {detail}',
  'feed.event.tendered': '{actor} handed in the work {detail}',
  'feed.event.tenderRetracted': '{actor} took the work back {detail}',
  'feed.event.dissolved': '{actor} cancelled {detail}',
  'feed.event.revived': '{actor} reopened {detail}',
  'feed.event.activityDeclared': '{actor} declared {detail}',
  'feed.event.concluded': '{actor} finished {detail}',
  'feed.event.cancelled': '{actor} cancelled {detail}',
  'feed.event.reopened': '{actor} reopened {detail}',
  'feed.event.seatOffered': '{actor} offered a seat {detail}',
  'feed.event.seatTaken': '{actor} took up a seat {detail}',
  'feed.event.seatDeclined': '{actor} declined a seat {detail}',
  'feed.event.seatWithdrawn': '{actor} withdrew a seat offer {detail}',
  'feed.event.seatOfferAccepted': '{actor} accepted a seat offer {detail}',
  /** A seat event that names its parts in place (`Entry.slots`): the holder
   *  as a person or a group, the seat as its chip (`feed.offer.role.*`). The
   *  words are shown, not only said. An offer is proposed; a seat filled
   *  without one is assigned, or taken up by its holder; the holder accepts
   *  an offer (a member for its group) or declines it. */
  'feed.event.seatProposed': '{actor} proposed {holder} {role}',
  'feed.event.seatAssigned': '{actor} assigned {holder} {role}',
  'feed.event.seatAccepted': '{actor} accepted {role}',
  'feed.event.seatAcceptedFor': '{actor} accepted {role} for {holder}',
  'feed.event.seatTookUp': '{actor} took up {role}',
  'feed.event.seatRefused': '{actor} declined {role}',
  'feed.event.pushRuleSet': '{actor} set assignment rules {detail}',
  'feed.event.reassigned': '{actor} reassigned {detail}',
  'feed.event.fieldChanged': '{actor} edited {detail}',
  'feed.event.contentEdited': '{actor} edited {detail}',
  'feed.event.commented': '{actor} commented {detail}',
  'feed.event.acknowledged': '{actor} acknowledged {detail}',
  'feed.event.resolved': '{actor} cleared the verdict {detail}',
  'feed.event.accepted': '{actor} accepted {detail}',
  'feed.event.disputed': '{actor} denied {detail}',
  'feed.event.closed': '{actor} closed {detail}',
  'feed.event.aborted': '{actor} aborted {detail}',
  'feed.event.dependencyAdded': '{actor} added dependency {detail}',
  'feed.event.dependencyRemoved': '{actor} removed dependency {detail}',
  'feed.event.reparented': '{actor} moved {detail}',
  'feed.event.relationshipAdded': '{actor} linked {detail}',
  'feed.event.relationshipRemoved': '{actor} unlinked {detail}',
  'feed.event.relationshipProposed': '{actor} requested a link {detail}',
  'feed.event.relationshipAccepted': '{actor} accepted a link {detail}',
  'feed.event.relationshipRejected': '{actor} declined a link {detail}',
  'feed.event.shared': '{actor} granted a role {detail}',
  'feed.event.unshared': '{actor} revoked a role {detail}',
  'feed.event.accessRequested': '{actor} requested access {detail}',
  'feed.event.stageAdvanced': '{actor} advanced a stage {detail}',
  'feed.event.memberAdded': '{actor} added a member {detail}',
  'feed.event.memberRemoved': '{actor} removed a member {detail}',
  'feed.event.nestingProposed': '{actor} proposed nesting {detail}',
  'feed.event.nestingAccepted': '{actor} accepted nesting {detail}',
  'feed.event.nestingSevered': '{actor} severed nesting {detail}',
  'feed.event.memberProposed': '{actor} asked a person to join {detail}',
  'feed.event.memberAccepted': '{actor} joined {detail}',
  'feed.event.memberDeclined': '{actor} declined to join {detail}',
  'feed.event.grantIssued': '{actor} granted access {detail}',
  'feed.event.grantRevoked': '{actor} revoked access {detail}',
  'feed.event.moduleEnabled': '{actor} enabled a module {detail}',
  'feed.event.moduleDisabled': '{actor} disabled a module {detail}',
  'feed.event.moduleAdded': '{actor} added a module {detail}',
  'feed.event.moduleRemoved': '{actor} removed a module {detail}',
  'feed.event.tagDefined': '{actor} defined a tag {detail}',
  'feed.event.tagApplied': '{actor} applied a tag {detail}',
  'feed.event.tagCleared': '{actor} cleared a tag {detail}',
  'feed.event.factQualified': '{actor} qualified a fact {detail}',
  'feed.event.factAdopted': '{actor} set {detail}',
  /** The person states it, so the word does not describe the person
   *  (`STATED_SENTENCES`, src/domain/entries.ts). */
  'feed.event.factContained': '{actor}: not affected by {detail}',
  'feed.event.factContainedAlone': '{actor}: not affected {detail}',
  'feed.event.flowFinding': '{actor} flagged a rule {detail}',
  'feed.event.factCrossed': '{actor} crossed a boundary {detail}',
  'feed.event.viewDefined': '{actor} saved a board or a view {detail}',
  'feed.event.viewUpdated': '{actor} updated a board or a view {detail}',
  'feed.event.viewDeleted': '{actor} deleted a board {detail}',
  'feed.event.viewRetired': '{actor} archived a board or a view {detail}',
  'feed.event.viewRestored': '{actor} restored a board or a view {detail}',
  'feed.event.viewAccessGranted': '{actor} shared a board or a view {detail}',
  'feed.event.viewAccessRevoked': '{actor} unshared a board or a view {detail}',
  'feed.event.proposalCreated': '{actor} drafted a proposal {detail}',
  'feed.event.proposalPublished': '{actor} published a proposal {detail}',
  'feed.event.proposalWithdrawn': '{actor} withdrew a proposal {detail}',
  'feed.event.changeProposed': '{actor} proposed a change {detail}',
  'feed.event.changeApplied': '{actor} applied a change {detail}',
  'feed.event.changeDeclined': '{actor} declined a proposed change {detail}',
  'feed.event.changeSuperseded': '{actor} superseded a proposed change {detail}',
  'feed.event.planCreated': '{actor} created a plan {detail}',
  'feed.event.proposalAddedToPlan': '{actor} added a proposal to a plan {detail}',
  'feed.event.planPublished': '{actor} published a plan {detail}',
  'feed.event.definitionDefined': '{actor} defined a flow {detail}',
  'feed.event.definitionChanged': '{actor} changed a definition {detail}',
  'feed.event.definitionArchived': '{actor} archived a definition {detail}',
  'feed.event.definitionUnarchived': '{actor} un-archived a definition {detail}',
  'feed.event.definitionAccessGranted': '{actor} granted access to a flow {detail}',
  'feed.event.definitionAccessRevoked': '{actor} revoked access to a flow {detail}',
  'feed.event.definitionAdopted': '{actor} adopted a definition {detail}',
  'feed.event.definitionForked': '{actor} forked a definition {detail}',
  'feed.event.definitionLinked': '{actor} declared an alignment {detail}',
  'feed.event.definitionUnlinked': '{actor} removed an alignment {detail}',
  'feed.event.annotationChanged': '{actor} noted {detail}',
  'feed.event.itemPinned': '{actor} pinned to board {detail}',
  'feed.event.itemUnpinned': '{actor} unpinned from board {detail}',
  'feed.event.flowJoined': '{actor} joined a flow {detail}',
  'feed.event.flowLeft': '{actor} left a flow {detail}',
  'feed.event.flowAdvanced': '{actor} advanced in a flow {detail}',
  'feed.event.flowStageMoved': '{actor} moved a stage {detail}',
  'feed.event.flowWaived': '{actor} waived a condition {detail}',
  'feed.event.flowConditionAdded': '{actor} added a condition {detail}',
  'feed.event.elevated': '{actor} elevated (override mode) {detail}',
  'feed.event.deElevated': '{actor} ended override mode {detail}',
  'feed.event.decisionRecorded': '{actor} recorded a decision {detail}',
  'feed.event.ceremonyConvened': '{actor} convened a ceremony {detail}',
  'feed.event.ceremonyItemAdded': '{actor} added a decision to the agenda {detail}',
  'feed.event.ceremonyItemExecuted': '{actor} took a decision {detail}',
  'feed.event.ceremonyItemDeclined': '{actor} declined a decision {detail}',
  'feed.event.ceremonyClosed': '{actor} closed a ceremony {detail}',
  'feed.event.ceremonyRosterChanged': '{actor} changed a ceremony role {detail}',
  'feed.event.inviteCreated': '{actor} invited someone by email {detail}',
  'feed.event.inviteRevoked': '{actor} revoked an invite {detail}',
  'feed.event.inviteClaimed': '{actor} joined by invite {detail}',
  'feed.event.inviteExpired': '{actor} let an invite expire {detail}',
  'feed.event.machineKeyMinted': '{actor} minted an agent key {detail}',
  'feed.event.machineKeyRevoked': '{actor} revoked an agent key {detail}',
  'feed.event.childrenReordered': '{actor} reordered children {detail}',
  'feed.event.closeDeferred': '{actor} deferred the close {detail}',
  'feed.event.closeDeferralRetracted': '{actor} took back the deferred close {detail}',
  'feed.event.itemTagged': '{actor} tagged {detail}',
  'feed.event.itemUntagged': '{actor} removed tag {detail}',
  'feed.event.attachmentAdded': '{actor} added a file {detail}',
  'feed.event.attachmentRemoved': '{actor} removed a file {detail}',
  'feed.event.attachmentCollected': "{actor} deleted a file's contents {detail}",
} as const;
